/**
 * CSV parsing, for bulk import (RX-ORG-08).
 *
 * Written rather than depended on, because the input is not really "CSV" --
 * it is whatever Excel produced when a care-home administrator chose "Save
 * As", and the failure modes are specific and known:
 *
 *   - A UTF-8 BOM at the start, which turns the first header into `﻿name`
 *     and makes the column silently unmatched.
 *   - CRLF line endings, so every last field on a line ends in `\r`.
 *   - Quoted fields containing commas, newlines, and doubled quotes.
 *   - A trailing newline, which must not produce a final empty row.
 *   - Tabs, when someone pasted from a spreadsheet rather than exporting.
 *
 * Every one of those produces a *plausible looking* wrong answer rather than
 * an error, which is the argument for handling them deliberately.
 */

/** A parsed row, with the 1-based line number it came from for error reporting. */
export interface CsvRow {
  line: number;
  cells: string[];
}

export interface CsvTable {
  header: string[];
  rows: CsvRow[];
  /** The delimiter that was detected. */
  delimiter: string;
}

const BOM = '﻿';

/**
 * Guess the delimiter from the header line.
 *
 * Counts only characters outside quotes: a header like `"Name, full",room`
 * is one comma, not two, and picking tab because the comma count looked odd
 * would split every subsequent row wrongly.
 */
export function detectDelimiter(firstLine: string): string {
  const candidates = [',', '\t', ';'];
  let best = ',';
  let bestCount = 0;
  for (const d of candidates) {
    let count = 0;
    let inQuotes = false;
    for (let i = 0; i < firstLine.length; i++) {
      const ch = firstLine[i];
      if (ch === '"') inQuotes = !inQuotes;
      else if (ch === d && !inQuotes) count++;
    }
    if (count > bestCount) {
      best = d;
      bestCount = count;
    }
  }
  return best;
}

/**
 * Parse a whole document.
 *
 * A single pass over the characters rather than splitting on newlines first,
 * because a quoted field may *contain* a newline and splitting would tear a
 * row in half. That is not hypothetical: an address or a note typed into
 * Excel with alt-enter does exactly this.
 */
export function parseCsv(input: string, delimiter?: string): CsvTable {
  const text = input.startsWith(BOM) ? input.slice(BOM.length) : input;

  // Detect on the first physical line, which is good enough: a delimiter
  // inside a quoted header is already handled by the scan above.
  const firstBreak = text.search(/\r?\n/);
  const firstLine = firstBreak === -1 ? text : text.slice(0, firstBreak);
  const delim = delimiter ?? detectDelimiter(firstLine);

  const rows: string[][] = [];
  const lineOf: number[] = [];

  let cells: string[] = [];
  let field = '';
  let inQuotes = false;
  let line = 1;
  let rowStartLine = 1;
  let sawAnyChar = false;

  const endField = () => {
    cells.push(field);
    field = '';
  };
  const endRow = () => {
    endField();
    rows.push(cells);
    lineOf.push(rowStartLine);
    cells = [];
    rowStartLine = line;
  };

  for (let i = 0; i < text.length; i++) {
    const ch = text[i]!;

    if (inQuotes) {
      if (ch === '"') {
        // A doubled quote inside a quoted field is one literal quote.
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        if (ch === '\n') line++;
        field += ch;
      }
      continue;
    }

    if (ch === '"' && field === '') {
      inQuotes = true;
      sawAnyChar = true;
      continue;
    }
    if (ch === delim) {
      endField();
      sawAnyChar = true;
      continue;
    }
    if (ch === '\r') {
      // CRLF: swallow the \r and let the \n end the row. A lone \r (classic
      // Mac) ends the row on its own.
      if (text[i + 1] === '\n') continue;
      line++;
      endRow();
      sawAnyChar = false;
      continue;
    }
    if (ch === '\n') {
      line++;
      endRow();
      sawAnyChar = false;
      continue;
    }
    field += ch;
    sawAnyChar = true;
  }

  // A trailing newline leaves nothing pending; anything else is a final row.
  if (sawAnyChar || field !== '' || cells.length > 0) endRow();

  const headerCells = rows.shift() ?? [];
  const headerLine = lineOf.shift();
  void headerLine;

  return {
    header: headerCells.map((h) => h.trim()),
    rows: rows
      .map((cells, i) => ({ line: lineOf[i] ?? i + 2, cells }))
      // A row of nothing but empty cells is a blank line in the middle of the
      // file, not a resident with no name.
      .filter((r) => r.cells.some((c) => c.trim() !== '')),
    delimiter: delim,
  };
}

/** Serialise, quoting only where it is needed. Used by the tests and the template. */
export function toCsv(header: string[], rows: string[][], delimiter = ','): string {
  const quote = (s: string) =>
    s.includes(delimiter) || s.includes('"') || s.includes('\n') || s.includes('\r')
      ? `"${s.replace(/"/g, '""')}"`
      : s;
  return [header, ...rows].map((r) => r.map(quote).join(delimiter)).join('\n');
}

/**
 * A cell a spreadsheet will not run — docs/33 B11.
 *
 * Statement lines carry words people typed (a skip reason, a note). A cell
 * starting with `=`, `+`, `-` or `@` is a formula to Excel and Sheets, so it
 * is given a leading apostrophe, which they show as plain text.
 */
export function inertCell(s: string): string {
  return /^[=+\-@\t\r]/.test(s) ? `'${s}` : s;
}

/**
 * The statement as a spreadsheet file — docs/33 B11.
 *
 * The same lines the screen and the PDF show, one per row, oldest first, with
 * a byte-order mark so Excel opens Hindi, Telugu and the rest as UTF-8 rather
 * than as mojibake. `header` is in the reader's language.
 */
export function statementCsv(
  header: [when: string, what: string, by: string, from: string],
  lines: readonly { when: string; text: string; by: string; from: string }[],
): string {
  return '﻿' + toCsv(header, lines.map((l) => [l.when, l.text, l.by, l.from].map(inertCell))) + '\n';
}
